<template>
  <div class="company-dashboard">
    <!-- Company Header -->
    <div class="company-header">
      <div class="container-fluid">
        <div class="row align-items-center">
          <div class="col-md-8">
            <div class="company-info">
              <h1 class="company-name">{{ company && company.name }}</h1>
            </div>
          </div>
          <div class="col-md-4 text-right">
            <div class="action-buttons">
              <b-button
                variant="success"
                size="sm"
                class="btn-erp-download-small"
                @click="openERPModal"
              >
                <i class="las la-download mr-1"></i>
                Download ERP
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div> 
    <!-- Analytics Section -->
    <div class="analytics-section">
      <div class="container-fluid">
        <b-row :class="['d-flex flex-row justify-content-center', this.isMobileDevice ?'':'flex-nowrap']"> 
          <b-col class="mx-3" :class="this.isMobileDevice ?'pb-4':''">
            <div class="analytics-card payables-card">
              <div class="card-header">
                <div class="card-title">
                  <i class="las la-credit-card mr-2"></i>
                  Payables Analytics
                </div>
                <date-range-picker
                  :startDate="dateRange[0]"
                  :endDate="dateRange[1]"
                  type="due"
                  class="date-picker"
                  :compact="true"
                  :customRanges="ranges"
                  @date-select="filterPayablesAnalyticsByDate"
                >
                </date-range-picker>
              </div>
              <div class="card-body">
                <donut-chart 
                  :toggleable="true"
                  :labels="labels" 
                  :series1="payables_count_series" 
                  :series2="payables_sum_series"
                  :currency="company && company.base_currency"
                  type="payables" 
                />
              </div>
            </div>
          </b-col>
          <b-col class="mx-3" v-if="company && company.is_merchant" :class="this.isMobileDevice ?'pt-4':''">
            <div class="analytics-card receivables-card">
              <div class="card-header">
                <div class="card-title">
                  <i class="las la-money-bill mr-2"></i>
                  Receivables Analytics
                </div>
                <date-range-picker
                  :startDate="dateRange[0]"
                  :endDate="dateRange[1]"
                  type="due"
                  class="date-picker"
                  :compact="true"
                  :customRanges="ranges"
                  @date-select="filterReceivablesAnalyticsByDate"
                >
                </date-range-picker>
              </div>
              <div class="card-body">
                <donut-chart 
                  :toggleable="true"
                  :labels="labels" 
                  :series1="receivables_count_series" 
                  :series2="receivables_sum_series"
                  :currency="company && company.base_currency"
                  type="receivables" 
                />
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </div>
    <!-- Invoices Section -->
    <div class="invoices-section">
      <div class="container-fluid">
        <div class="section-header">
          <h2 class="section-title">
            <i class="las la-file-invoice mr-2"></i>
            Invoices Management
          </h2>
        </div>
        <div class="invoices-card">   
          <div class="filters-section">
            <table-filter
              :show_invoices_filter="show_invoices_filter"
              :dueDateUpdateCallback="filterInvoicesByDueDate"
              :issueDateUpdateCallback="filterInvoicesByIssueDate"
              @filter1-change="filterInvoicesByType"
              @filter2-change="filterInvoicesByStatus"
              @filter3-change="filterInvoicesByCurrency"
              @reset="resetFilters"
            >
            </table-filter>
          </div>
          <div class="table-section">
            <invoice-table
              :invoices="filteredInvoices"
              :withTypeColumn="true"
              :paginationNavFunction="setAllInvoices"
              :havePagination="true"
              @column-sort="sortInvoicesbyColumn"
            ></invoice-table>
          </div>
        </div>
      </div>
    </div>

    <!-- ERP Modal -->
    <erp-modal ref="erp_modal" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import InvoiceTable from './components/InvoiceTable.vue';
import DonutChart from './components/DonutChart.vue';
import DateRangePicker from './components/DateRangePicker.vue'
import TableFilter from './components/filter.vue';
import ERPModal from './components/erpModal.vue';

import CompaniesService from '@/services/companies.service';
import PayablesService from '@/services/payables.service';
import ReceivablesService from '@/services/receivables.service';

import { GET_ALL_INVOICES, UPDATE_ALL_INVOICES } from '@/store/invoice';
import { DateRanges, thisMonth, last_30_days, last_7_days, next_30_days } from '@/dateRanges.js'
import { QUERY_PARAMETERS, SHOW_INVOICES_FILTER } from '@/constants';

export default {
  data() {
    return {
      company: null,
      invoices: null,
      dateRange: thisMonth,
      ranges: {
        'All' : DateRanges['All Time'],
        'Last 7 Days' : last_7_days,
        'Last 30 Days' : last_30_days,
        'Next 30 Days' : next_30_days,        
        'This Year' : DateRanges['This Year'],
      },

      labels: ["Due", "Cooling", "Overdue", "Paid", "Settled"],
      receivables_count_series: [],
      receivables_sum_series: [],
      payables_count_series: [],
      payables_sum_series: [],
      isMobileDevice:false,
      show_invoices_filter: SHOW_INVOICES_FILTER['Dashboard'],
    }
  },
  components: {
    InvoiceTable,
    DonutChart,
    DateRangePicker,
    TableFilter,
    'erp-modal': ERPModal,
  },
  created(){
      let regexp = /android|iphone|kindle|ipad/i; 
      this.isMobileDevice = regexp.test(navigator.userAgent)||  screen.width <= 991 ; 
  },
  mounted() {
    CompaniesService.get(this.$route.params.business_id)
    .then(({data}) => {
      this.company = data;

      this.$store.dispatch(GET_ALL_INVOICES, {page: 1, slug: this.$route.params.business_id, params: {}})
      .catch((err) => {
        if (err.status === 403) {
          let redir_link = localStorage.getItem("perm_redir_link");
          let redir_query = localStorage.getItem("perm_query_params");
          this.$router.push({path: redir_link, query: JSON.parse(redir_query)});
        }
      });
    })
    .catch((err) => {
      console.log(err);
      this.$showAlert("Something went wrong!",'danger');
    });
  },
  methods: {
    updateInvoices(params) {
      this.$store.dispatch(UPDATE_ALL_INVOICES, {
        slug: this.$route.params.business_id,
        params: params,
      })
    },
    filterInvoicesByType(type) {
      this.updateInvoices({
        [QUERY_PARAMETERS.TYPE]: type.displayTitle,
        currentPath: this.$route.path,
      });
    },
    filterInvoicesByStatus(statusObject) {
      this.updateInvoices({
        [QUERY_PARAMETERS.STATUS]: statusObject.value,
        currentPath: this.$route.path,
      });
    },
    filterInvoicesByDueDate(dateObject) {
      this.updateInvoices({
        [QUERY_PARAMETERS.START_TIME]: dateObject.startDate,
        [QUERY_PARAMETERS.END_TIME]: dateObject.endDate,
        currentPath: this.$route.path,
      }); 
    },
    filterInvoicesByIssueDate(dateObject) {
      this.updateInvoices({
        [QUERY_PARAMETERS.ISSUE_START_TIME]: dateObject.startDate,
        [QUERY_PARAMETERS.ISSUE_END_TIME]: dateObject.endDate,
        currentPath: this.$route.path,
      }); 
    },
    filterInvoicesByCurrency(currencyObject) {
      this.updateInvoices({
        [QUERY_PARAMETERS.CURRENCY]: currencyObject.value,
        currentPath: this.$route.path,
      }); 
      this.currentCurrency = currencyObject.value;
      this.$route.query[QUERY_PARAMETERS.CURRENCY] = currencyObject.value;
    },
    sortInvoicesbyColumn(columnValue){
      this.updateInvoices({
        [QUERY_PARAMETERS.SORT]: columnValue,
        currentPath: this.$route.path,
      });
    },
    setAllInvoices(pageNoOrType) {
      this.updateInvoices({
        [QUERY_PARAMETERS.PAGE_NO]: pageNoOrType,
        currentPath: this.$route.path,
      });
    },
    resetFilters() {
      this.$store.dispatch(GET_ALL_INVOICES, {slug: this.$route.params.business_id, params: {page: 1}});
    },
    filterPayablesAnalyticsByDate(date_range) {
      let params = {
        "due_start": date_range.startDate,
        "due_end": date_range.endDate
      };
      PayablesService.get_analytics(this.$route.params.business_id, params)
      .then((data) => {
        this.payables_count_series = [
          data.data.due_count || 0,
          data.data.cooling_count || 0,
          data.data.overdue_count || 0,
          data.data.paid_count || 0,
          data.data.settled_count || 0
        ];
        this.payables_sum_series = [
          data.data.due_sum || 0,
          data.data.cooling_sum || 0,
          data.data.overdue_sum || 0,
          data.data.paid_sum || 0,
          data.data.settled_sum || 0
        ];
      });
    },
    filterReceivablesAnalyticsByDate(date_range) {
      let params = {
        "due_start": date_range.startDate,
        "due_end": date_range.endDate
      };
      ReceivablesService.get_analytics(this.$route.params.business_id, params)
      .then((data) => {
        this.receivables_count_series = [
          data.data.due_count || 0,
          data.data.cooling_count || 0,
          data.data.overdue_count || 0,
          data.data.paid_count || 0,
          data.data.settled_count || 0
        ];
        this.receivables_sum_series = [
          data.data.due_sum || 0,
          data.data.cooling_sum || 0,
          data.data.overdue_sum || 0,
          data.data.paid_sum || 0,
          data.data.settled_sum || 0
        ];
      });
    },
    openERPModal() {
      if (!this.company) {
        this.$showAlert('Company data is not loaded yet. Please try again.', 'warning');
        return;
      }

      this.$nextTick(() => {
        if (this.$refs.erp_modal) {
          if (typeof this.$refs.erp_modal.show === 'function') {
            this.$refs.erp_modal.show(this.company);
          } else {
            console.error('ERP Modal show method not found. Available methods:', Object.getOwnPropertyNames(this.$refs.erp_modal));
          }
        } else {
          console.error('ERP Modal component reference not found');
          this.$showAlert('ERP Modal is not available. Please refresh the page and try again.', 'danger');
        }
      });
    },
  },
  computed: mapState({
    filteredInvoices(state) {
      return state.invoice.allInvoices;
    },
  })
};
</script>

<style scoped lang="scss"> 
@import "./company.scss";
</style>